import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

/**
 * Context for managing navigation state and pending transactions
 * Particularly useful for flows where users navigate away (e.g., to add funds)
 * and need to return to complete a transaction
 */
const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {
  // Main state for pending transactions (purchases, investments, etc.)
  const [pendingTransaction, setPendingTransaction] = useState(null);

  // Navigation history tracking
  const [lastVisitedPages, setLastVisitedPages] = useState([]);

  // Return URL for completing transactions after adding funds
  const [returnUrl, setReturnUrl] = useState(null);

  // Flag to determine if user is returning from adding funds
  const [isReturningFromWallet, setIsReturningFromWallet] = useState(false);

  // Load persisted state from localStorage on mount
  useEffect(() => {
    try {
      const savedTransaction = localStorage.getItem("pendingTransaction");
      const savedReturnUrl = localStorage.getItem("returnUrl");

      if (savedTransaction) {
        setPendingTransaction(JSON.parse(savedTransaction));
      }

      if (savedReturnUrl) {
        setReturnUrl(savedReturnUrl);
      }
    } catch (error) {
      console.error("Error restoring navigation state:", error);
      // Clear corrupted data
      localStorage.removeItem("pendingTransaction");
      localStorage.removeItem("returnUrl");
    }
  }, []);

  // Save pending transaction to localStorage when it changes
  useEffect(() => {
    if (pendingTransaction) {
      localStorage.setItem(
        "pendingTransaction",
        JSON.stringify(pendingTransaction)
      );
    } else {
      localStorage.removeItem("pendingTransaction");
    }
  }, [pendingTransaction]);

  // Save return URL to localStorage when it changes
  useEffect(() => {
    if (returnUrl) {
      localStorage.setItem("returnUrl", returnUrl);
    } else {
      localStorage.removeItem("returnUrl");
    }
  }, [returnUrl]);

  /**
   * Store transaction details when navigating away to add funds
   * @param {Object} details - Transaction details including type, amount, product info
   * @param {string} sourceUrl - URL to return to after adding funds
   */
  const setPendingPurchase = useCallback((transaction, sourceUrl = null) => {
    // If it's a referral payout, handle it directly
    if (
      transaction.type === "REFERRAL_PAYOUT" ||
      transaction.type === "REFERRAL_BULK_PAYOUT"
    ) {
      setPendingTransaction(transaction);
      return;
    }

    // Otherwise, set with timestamp and optional sourceUrl
    setPendingTransaction({
      ...transaction,
      timestamp: new Date().toISOString(),
      sourceUrl,
    });
  }, []);

  /**
   * Clear transaction when completed or abandoned
   */
  const clearPendingPurchase = useCallback(() => {
    setPendingTransaction(null);
    setReturnUrl(null);
    setIsReturningFromWallet(false);
  }, []);

  /**
   * Track page visits for better navigation history
   * @param {string} path - Current page path
   */
  const trackPageVisit = useCallback((path) => {
    setLastVisitedPages((prev) => {
      // Don't add duplicate consecutive entries
      if (prev.length > 0 && prev[0] === path) return prev;

      // If navigating from wallet to a previous page, set returning flag
      if (
        prev.length > 0 &&
        prev[0].includes("/wallet") &&
        !path.includes("/wallet")
      ) {
        setIsReturningFromWallet(true);
      } else if (path.includes("/wallet")) {
        // Reset the flag when navigating to wallet
        setIsReturningFromWallet(false);
      }

      // Keep only last 5 visited pages
      const newHistory = [path, ...prev.slice(0, 4)];
      return newHistory;
    });
  }, []);

  /**
   * Get the last meaningful page (filtering out wallet and auth pages)
   * Useful for returning users to their previous context
   */
  const getLastMeaningfulPage = useCallback(() => {
    const nonSystemPages = lastVisitedPages.filter(
      (page) =>
        !page.includes("/wallet") &&
        !page.includes("/Signin-Signup") &&
        !page.includes("/register") &&
        !page.includes("/kyc")
    );

    return nonSystemPages.length > 0 ? nonSystemPages[0] : "/";
  }, [lastVisitedPages]);

  /**
   * Check if user has sufficient funds for pending transaction
   * @param {number} currentBalance - User's current wallet balance
   * @returns {boolean} - Whether user has sufficient funds
   */
  const hasSufficientFundsForPending = useCallback(
    (currentBalance) => {
      if (!pendingTransaction || currentBalance === null) return true;

      // Handle different transaction types
      switch (pendingTransaction.type) {
        case "PRODUCT_PURCHASE":
          return currentBalance >= pendingTransaction.totalCost;
        case "MUTUAL_FUND_INVESTMENT":
          return currentBalance >= pendingTransaction.amount;
        default:
          return true;
      }
    },
    [pendingTransaction]
  );

  /**
   * Get return URL for completing transaction after adding funds
   * Falls back to last meaningful page if no specific return URL is set
   */
  const getReturnDestination = useCallback(() => {
    return returnUrl || getLastMeaningfulPage();
  }, [returnUrl, getLastMeaningfulPage]);

  // Value object provided to consumers of the context
  const value = {
    pendingTransaction,
    setPendingPurchase,
    clearPendingPurchase,
    trackPageVisit,
    getLastMeaningfulPage,
    isReturningFromWallet,
    hasSufficientFundsForPending,
    getReturnDestination,
    lastVisitedPages,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to access the navigation context
 * @returns {Object} Navigation context values and methods
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};

// {
//   /* Table Section */
// }
// {/* <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
//   {loading || filterLoading ? (
//     <div className="p-8 flex flex-col items-center justify-center">
//       <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
//       <p className="text-gray-600">Loading data...</p>
//     </div>
//   ) : error ? (
//     <div className="p-8 text-center text-red-500">
//       <svg
//         className="mx-auto h-12 w-12 text-red-400"
//         fill="none"
//         viewBox="0 0 24 24"
//         stroke="currentColor"
//       >
//         <path
//           strokeLinecap="round"
//           strokeLinejoin="round"
//           strokeWidth="2"
//           d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
//         />
//       </svg>
//       <p className="mt-2 font-medium">{error}</p>
//     </div>
//   ) : (
//     <div className="overflow-x-auto">
//       <table className="min-w-full divide-y divide-gray-200">
//         <thead className="bg-gray-50">
//           <tr>
//             <th
//               scope="col"
//               className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//             >
//               Name
//             </th>
//             <th
//               scope="col"
//               className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//             >
//               Sector
//             </th>
//             <th
//               scope="col"
//               className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//             >
//               Industry
//             </th>
//             <th
//               scope="col"
//               className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//             >
//               Buy Price
//             </th>

//             {Object.keys(filterOptions)
//               .filter(
//                 (key) =>
//                   ![
//                     "price",
//                     "industry",
//                     "sector",
//                     "marketCap",
//                     "revenue",
//                     "peRatio",
//                     "debtToEquity",
//                   ].includes(key.toLowerCase())
//               )
//               .map((key) => (
//                 <th
//                   key={key}
//                   scope="col"
//                   className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//                 >
//                   {getFilterDisplayName(key)}
//                 </th>
//               ))}

//             <th
//               scope="col"
//               className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
//             >
//               Actions
//             </th>
//           </tr>
//         </thead>
//         <tbody className="bg-white divide-y divide-gray-200">
//           {filteredData.map((stock, index) => (
//             <tr
//               key={stock.id || index}
//               className="hover:bg-gray-50 transition-colors cursor-pointer"
//               onClick={() => handleRedirect(stock.uniqueTitle)}
//             >
//               <td className="px-6 py-4 whitespace-nowrap">
//                 <div className="text-sm font-medium text-gray-900">
//                   {stock.name}
//                 </div>
//               </td>
//               <td className="px-6 py-4 whitespace-nowrap">
//                 <div className="text-sm text-gray-500">{stock.sector}</div>
//               </td>
//               <td className="px-6 py-4 whitespace-nowrap">
//                 <div className="text-sm text-gray-500">{stock.industry}</div>
//               </td>
//               <td className="px-6 py-4 whitespace-nowrap">
//                 <div className="text-sm text-gray-900">{stock.buyPrice}</div>
//               </td>

//               {Object.keys(filterOptions)
//                 .filter(
//                   (key) =>
//                     ![
//                       "price",
//                       "industry",
//                       "sector",
//                       "marketCap",
//                       "revenue",
//                       "peRatio",
//                       "debtToEquity",
//                     ].includes(key.toLowerCase())
//                 )
//                 .map((key) => (
//                   <td key={key} className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm text-gray-500">
//                       {getStockValueForFilter(stock, key) !== null &&
//                       getStockValueForFilter(stock, key) !== undefined
//                         ? getStockValueForFilter(stock, key).toString()
//                         : "N/A"}
//                     </div>
//                   </td>
//                 ))}

//               <td className="px-6 py-4 whitespace-nowrap">
//                 <button
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleRedirect(stock.uniqueTitle);
//                   }}
//                   className="text-blue-600 hover:text-blue-800 text-sm font-medium"
//                 >
//                   View Details →
//                 </button>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   )}
// </div>; */}
